import _ from 'lodash';
import { useState } from 'react';
import { styled } from 'styled-components';

import { Col, Container, Modal, Row } from 'react-bootstrap';

import { useSelector } from 'react-redux';
import * as store from '../../../../reducers/store';

import { Link, Typography } from '@vivaahealth/design-system';

import EmptySearch from '../Common/SearchBar/EmptySearch';
import SearchResultItem from '../Common/SearchBar/SearchResultItem';
import {
  CategoryType,
  SearchItem,
  StyledBorder,
  StyledHeaderSmall,
} from '../Common';

interface Props {
  insurances?: string[];
  insurancesRef?: React.RefObject<HTMLDivElement>;
}

interface ModalProps {
  insurances: string[];
  insuranceSearchItems: SearchItem[];
  viewAll: boolean;
  focus: boolean;
  handleViewAll: () => void;
  setFocus: (value: boolean) => void;
}

const Insurances: React.FC<Props> = ({ insurances, insurancesRef }) => {
  const [viewAll, setViewAll] = useState(false);
  const [focus, setFocus] = useState<boolean>(false);
  const embedded = useSelector((store: store.All) => store.embedded);

  const handleViewAll = () => {
    setViewAll((viewAll) => !viewAll);
  };

  if (!insurances || insurances.length < 1) {
    return null;
  }

  const insuranceSearchItems: SearchItem[] = insurances.map((insurance) => {
    return {
      categoryType: CategoryType.Insurance,
      name: insurance,
    };
  });

  const heading = (
    <>
      <StyledHeaderSmall variant="headline-small" bold>
        Insurances
      </StyledHeaderSmall>
    </>
  );

  const content = (
    <Row xs={1} lg={3} className="g-2 me-0">
      {insurances.slice(0, 17).map((ins, i) => (
        <Col key={ins}>
          <Typography variant="paragraph-large">{ins}</Typography>
        </Col>
      ))}
      {insurances.length > 17 && (
        <Col className="d-flex">
          <Typography variant="paragraph-large">
            +{insurances.length - 17} more
          </Typography>
          <StyledLink className="link-secondary ps-1" onClick={handleViewAll}>
            View all accepted insurances
          </StyledLink>
        </Col>
      )}
    </Row>
  );

  const modal = (
    <InsurancesModal
      insurances={insurances}
      insuranceSearchItems={insuranceSearchItems}
      viewAll={viewAll}
      focus={focus}
      handleViewAll={handleViewAll}
      setFocus={setFocus}
    />
  );

  return (
    <>
      <StyledBorder />
      {embedded ? (
        <div ref={insurancesRef}>
          {heading}
          {content}
          {modal}
        </div>
      ) : (
        <div ref={insurancesRef}>
          {heading}
          <Container className="px-0">
            {content}
            {modal}
          </Container>
        </div>
      )}
    </>
  );
};

export default Insurances;

const InsurancesModal: React.FC<ModalProps> = ({
  insurances,
  insuranceSearchItems,
  viewAll,
  focus,
  handleViewAll,
  setFocus,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [searchSuggestions, setSearchSuggestions] = useState<SearchItem[]>([]);

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    if (newValue.length > 0) {
      const debounceFilter = _.debounce(() => {
        if (insurances) {
          const suggestions = getInsuranceSuggestions(
            insuranceSearchItems,
            newValue,
          );
          setSearchSuggestions(suggestions);
        }
      }, 150);
      debounceFilter();
    }
    setInputValue(newValue);
    if (newValue === '') {
      setSearchSuggestions([]);
    }
  };

  const results = () => {
    if (inputValue === '') {
      return (
        <ResultsContainer>
          {insuranceSearchItems.map((item) => {
            return (
              <SearchResultItem
                key={item.name}
                inputValue={inputValue}
                item={item}
                handleItemClick={() => {}}
              />
            );
          })}
        </ResultsContainer>
      );
    }
    if (inputValue !== '' && searchSuggestions.length > 0) {
      return (
        <ResultsContainer>
          {searchSuggestions.map((item) => {
            return (
              <SearchResultItem
                key={item.name}
                inputValue={inputValue}
                item={item}
                handleItemClick={() => {}}
              />
            );
          })}
        </ResultsContainer>
      );
    } else if (searchSuggestions.length === 0 && inputValue.length > 0) {
      return <EmptySearch inputValue={inputValue} />;
    }
    const results = (
      <ResultsContainer>
        {searchSuggestions.map((item) => {
          return (
            <SearchResultItem
              key={item.name}
              inputValue={inputValue}
              item={item}
              handleItemClick={() => {}}
            />
          );
        })}
      </ResultsContainer>
    );
    return results;
  };

  return (
    <StyledModal show={viewAll} onHide={handleViewAll} centered>
      <Modal.Header closeButton className="py-3 px-4">
        <Modal.Title>
          <Typography variant="title-large" bold>
            Insurance plan types accepted
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <SearchContainer>
          <StyledTextField
            placeholder="Insurance name"
            value={inputValue}
            onMouseDown={() => {
              const suggestions = getInsuranceSuggestions(
                insuranceSearchItems,
                inputValue,
              );
              setSearchSuggestions(suggestions);
            }}
            onFocus={(e) => {
              setFocus(true);
              e.target.select();
            }}
            onBlur={() => {
              setFocus(false);
            }}
            inputMode="text"
            onChange={(event) => updateInputValue(event)}
          />
        </SearchContainer>
        {results()}
        {!focus && (
          <ResultsContainer>
            {insuranceSearchItems.map((item) => {
              return (
                <SearchResultItem
                  key={item.name}
                  inputValue={inputValue}
                  item={item}
                  handleItemClick={() => {}}
                />
              );
            })}
          </ResultsContainer>
        )}
      </Modal.Body>
    </StyledModal>
  );
};

const getInsuranceSuggestions = (
  insuranceSearchItems: SearchItem[],
  inputValue: string,
) => {
  return insuranceSearchItems.filter((searchItem) => {
    return searchItem.name.toLowerCase().includes(inputValue.toLowerCase());
  });
};

const StyledLink = styled(Link).attrs<{ color: string }>((props) => ({
  color: props.color || 'var(--color-primary)',
}))`
  color: ${(props) => props.color} !important;
  cursor: pointer;
  padding-top: 2px;
  text-decoration-line: underline;
`;

const StyledModal = styled(Modal)`
  --bs-modal-border-radius: 4px;
  & .modal-dialog {
    max-width: 680px;
  }
  & .modal-content {
    height: 464px;
    min-height: 464px;
  }
  & .modal-body {
    overflow: hidden;
    padding-bottom: 20px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
`;

const StyledTextField = styled.input`
  position: sticky;
  width: 100%;
  padding: 10px 16px;
  border: 1px solid var(--color-shade-60);
  border-radius: 4px;
  :focus {
    outline: none;
  }
`;

const ResultsContainer = styled.div`
  width: 100%;
  height: 85%;
  margin: 12px 0px;
  overflow: auto;
`;
